import { useCallback, useEffect, useState } from 'react';
import type { ApiKeyRecord } from '@hub/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ActionButton, Alert, Card, EmptyRow, Loading, Pill, SectionTitle, fmtDate, useFlash } from '../components/ui';
import { TwoFactorCard } from '../components/TwoFactorCard';

interface NewKey extends ApiKeyRecord { token: string }

export function AccountPage() {
  const { user, refresh } = useAuth();
  const [keys, setKeys] = useState<ApiKeyRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  // Shown once, right after creation — the server only keeps a hash.
  const [issued, setIssued] = useState<NewKey | null>(null);
  const flash = useFlash();

  const load = useCallback(async () => {
    try {
      setKeys(await api.get<ApiKeyRecord[]>('/api/keys'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load API keys');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function createKey() {
    setCreating(true);
    try {
      const key = await api.post<NewKey>('/api/keys', { name: name.trim() || 'default' });
      setIssued(key);
      setName('');
      await load();
    } catch (err) {
      flash.show(err instanceof Error ? err.message : 'Could not create the key', 'error');
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: 0, marginBottom: '1.5rem' }}>Account</h2>

      <Card style={{ marginBottom: '1.5rem' }}>
        <SectionTitle>Profile</SectionTitle>
        {user && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', fontSize: '0.85rem' }}>
            <div><div className="muted">Username</div><div className="mono">{user.username}</div></div>
            <div><div className="muted">Email</div><div className="mono">{user.email}</div></div>
            <div>
              <div className="muted">Role</div>
              <div><Pill tone={user.role === 'admin' ? 'emerald' : 'muted'}>{user.role}</Pill></div>
            </div>
            <div>
              <div className="muted">Email verified</div>
              <div><Pill tone={user.emailVerified ? 'emerald' : 'red'}>{user.emailVerified ? 'verified' : 'unverified'}</Pill></div>
            </div>
            <div>
              <div className="muted">Two-factor</div>
              <div><Pill tone={user.twoFactorEnabled ? 'emerald' : 'muted'}>{user.twoFactorEnabled ? 'enabled' : 'disabled'}</Pill></div>
            </div>
          </div>
        )}
      </Card>

      <TwoFactorCard onChanged={() => { void refresh(); }} />

      <Card>
        <SectionTitle actions={flash.node}>API keys</SectionTitle>

        <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
          The REST API accepts two credentials: a session cookie from signing in, or an API key.
          A key carries your role, so an admin&apos;s key is an admin credential. Passwords are only
          accepted by the login endpoint, never on ordinary requests.
        </p>

        <pre className="output" style={{ maxHeight: 'none', marginBottom: '1rem' }}>
{`# API key — the simplest option for scripts and CI
curl -H 'Authorization: Bearer hub_…' ${location.origin}/api/instances
curl -H 'X-API-Key: hub_…'            ${location.origin}/api/instances

# Or sign in once and reuse the session cookie
curl -c jar -X POST ${location.origin}/api/auth/login \\
  -H 'Content-Type: application/json' \\
  -d '{"login":"${user?.username ?? 'you'}","password":"…"}'
curl -b jar ${location.origin}/api/instances`}
        </pre>

        {issued && (
          <div className="alert-success" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
            <div style={{ marginBottom: '0.4rem' }}>
              Copy <strong>{issued.name}</strong> now — it is shown once and cannot be retrieved later.
            </div>
            <code className="mono" style={{ wordBreak: 'break-all' }}>{issued.token}</code>
            <div style={{ marginTop: '0.5rem' }}>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => { void navigator.clipboard?.writeText(issued.token).then(() => flash.show('Copied', 'ok')); }}
              >Copy</button>
              <button type="button" className="btn-ghost btn-sm" style={{ marginLeft: '0.4rem' }} onClick={() => setIssued(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 14rem' }}>
            <label className="form-label" htmlFor="key-name">Key name</label>
            <input
              id="key-name" className="form-input" placeholder="laptop, ci, …"
              value={name} onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button type="button" className="btn-primary" disabled={creating} onClick={() => { void createKey(); }}>
            {creating ? 'Generating…' : 'Generate key'}
          </button>
        </div>

        {error ? <Alert tone="error">{error}</Alert>
          : !keys ? <Loading />
          : (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr><th>Name</th><th>Prefix</th><th>Created</th><th>Last used</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {keys.length === 0 ? (
                    <EmptyRow colSpan={5}>No API keys yet.</EmptyRow>
                  ) : keys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.name}</td>
                      <td className="mono muted">{k.key_prefix}…</td>
                      <td className="muted">{fmtDate(k.created_at)}</td>
                      <td className="muted">{k.last_used_at ? fmtDate(k.last_used_at) : 'never'}</td>
                      <td>
                        <ActionButton
                          danger
                          busyLabel="Revoking…"
                          confirm={`Revoke "${k.name}"? Anything using it stops working immediately.`}
                          onClick={async () => {
                            await api.delete(`/api/keys/${k.id}`);
                            await load();
                          }}
                        >Revoke</ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </>
  );
}
